import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Chip,
  CircularProgress,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PageLayout from '../../components/layout/PageLayout';
import api from '../../api/config';
import { TASK_TEMPLATES } from '../../api/endpoints';
import { toast } from 'react-toastify';
import { buildTaskTemplateRelationParams } from './taskTemplateApiHelpers';

const row = (label, value) => (
  <Grid item xs={12} sm={6}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Grid>
);

const priorityColor = (priority) => {
  const p = String(priority || '').toLowerCase();
  if (p === 'high') return 'error';
  if (p === 'medium') return 'warning';
  if (p === 'low') return 'success';
  return 'default';
};

const ViewTaskTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [item, setItem] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(TASK_TEMPLATES.BY_ID(id), {
          params: buildTaskTemplateRelationParams(),
        });
        const payload = data?.data ?? data;
        if (!cancelled) setItem(payload);
      } catch (err) {
        toast.error(err.response?.data?.message || 'Failed to load task template');
        navigate('/task-templates');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (loading || !item) {
    return (
      <PageLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/task-templates')} sx={{ mb: 2 }}>
          Back to task templates
        </Button>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
            flexWrap: 'wrap',
            gap: 1,
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
            {item.title ?? 'Task template'}
          </Typography>
          <Button variant="outlined" size="small" onClick={() => navigate(`/task-templates/edit/${id}`)}>
            Edit
          </Button>
        </Box>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Priority
                </Typography>
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    size="small"
                    label={item.priority ?? '—'}
                    color={priorityColor(item.priority)}
                    variant="outlined"
                  />
                </Box>
              </Grid>
              {row('Author', item.author?.name ?? item.author?.username)}
              {row('Created at', item.created_at)}
              {row('Updated at', item.updated_at)}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" display="block">
                  Description
                </Typography>
                <Typography variant="body1" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                  {item.description ?? '—'}
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default ViewTaskTemplate;
